// Shared utility for accessing cached news data
// Used by Portfolio, Watchlist, and other components to avoid duplicate API calls

import { shouldUseFreshClientCache } from './clientCacheReload';

const CACHE_KEY = 'newsDataCache';
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes in milliseconds

// Track in-flight requests by date to coordinate multiple components
const inFlightRequests = new Map(); // Map<date, { promise: Promise, timestamp: number }>

export const clearNewsClientCache = () => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${CACHE_KEY}_`)) {
        localStorage.removeItem(key);
      }
    }
    inFlightRequests.clear();
    console.log('[NEWS CACHE] Cleared news client cache');
  } catch (error) {
    console.warn('[NEWS CACHE] Error clearing cache:', error);
  }
};

const isNonEmptyNewsData = (data) => Array.isArray(data) && data.length > 0;

const cleanupNonCurrentNewsCaches = (currentDate) => {
  try {
    // Iterate backward because removeItem changes localStorage indexes.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${CACHE_KEY}_`)) continue;
      if (key !== `${CACHE_KEY}_${currentDate}`) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.warn(`[CACHE CLEANUP] Failed to clean stale news cache keys:`, error);
  }
};

/**
 * Get news data for a specific date, using cache if available and valid
 * Coordinates requests so if a request was sent less than 20s ago, all components wait for the same request
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {boolean} useCache - Whether to check cache first (default: true)
 * @returns {Promise<Array>} News data array
 */
export const getCachedNewsData = async (date, useCache = true) => {
  if (!date) {
    return [];
  }

  if (useCache && shouldUseFreshClientCache()) {
    useCache = false;
  }

  const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York'});
  const now = Date.now();
  cleanupNonCurrentNewsCaches(currentDate);
  
  // STEP 1: Join any in-flight request for this date until it completes (Athena often takes >30s)
  const inFlightRequest = inFlightRequests.get(date);
  
  if (inFlightRequest) {
    const requestAge = now - inFlightRequest.timestamp;
    console.log(`[COORDINATION] ${date}: Waiting for in-flight request (age: ${Math.round(requestAge / 1000)}s)`);
    try {
      const data = await inFlightRequest.promise;
      console.log(`[COORDINATION] ${date}: Received data from coordinated request (${data?.length || 0} items)`);
      return data || [];
    } catch (error) {
      console.warn(`[COORDINATION] ${date}: In-flight request failed, will retry:`, error);
      if (inFlightRequests.get(date) === inFlightRequest) {
        inFlightRequests.delete(date);
      }
    }
  }
  
  // STEP 2: Check cache if it's the current date and useCache is true
  if (useCache && date === currentDate) {
    try {
      const cachedData = localStorage.getItem(`${CACHE_KEY}_${date}`);
      if (cachedData) {
        const parsedCache = JSON.parse(cachedData);
        if (
          !parsedCache ||
          typeof parsedCache.timestamp !== 'number' ||
          !Array.isArray(parsedCache.data) ||
          parsedCache.date !== date
        ) {
          console.warn(`[CACHE] ${date}: Invalid cache shape, removing`);
          localStorage.removeItem(`${CACHE_KEY}_${date}`);
        } else {
        const cacheAge = now - parsedCache.timestamp;
        
        // Use cached data if it's less than 2 minutes old
        if (cacheAge < CACHE_TTL) {
          console.log(`[CACHE] ${date}: Using cached data (age: ${Math.round(cacheAge / 1000)}s, ${parsedCache.data?.length || 0} items)`);
          return parsedCache.data || [];
        } else {
          console.log(`[CACHE] ${date}: Cache expired (age: ${Math.round(cacheAge / 1000)}s), will fetch fresh data`);
        }
        }
      } else {
        console.log(`[CACHE] ${date}: No cache found, will fetch fresh data`);
      }
    } catch (cacheError) {
      console.warn(`[CACHE] ${date}: Error reading cache:`, cacheError);
      // Continue to fetch if cache read fails
    }
  }
  
  // STEP 3: Create a new request promise (cache expired or no cache)
  console.log(`[REQUEST] ${date}: Starting new API request`);
  
  const fetchPromise = (async () => {
    try {
      const response = await fetch(`/api/news/${date}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch news: ${response.statusText}`);
      }
      const data = await response.json();
      
      console.log(`[REQUEST] ${date}: Received ${data?.length || 0} items from server`);
      
      // Cache only non-empty current-date data, so empty API responses do not overwrite valid cache
      if (date === currentDate) {
        try {
          if (isNonEmptyNewsData(data)) {
            const cacheData = {
              data: data,
              timestamp: Date.now(),
              date: date
            };
            localStorage.setItem(`${CACHE_KEY}_${date}`, JSON.stringify(cacheData));
            console.log(`[CACHE] ${date}: Saved to cache (${data?.length || 0} items)`);
          } else {
            console.log(`[CACHE] ${date}: API returned empty news data, skipping cache write`);
          }
        } catch (cacheError) {
          console.warn(`[CACHE] ${date}: Error saving cache:`, cacheError);
          // Continue even if caching fails
        }
      }
      
      return data || [];
    } catch (error) {
      console.error(`[REQUEST] ${date}: Error fetching news data:`, error);
      throw error; // Re-throw so waiting components know it failed
    } finally {
      // Remove from in-flight requests after completion (success or failure)
      inFlightRequests.delete(date);
      console.log(`[COORDINATION] ${date}: Request completed, removed from in-flight tracking`);
    }
  })();
  
  // Store the in-flight request with timestamp BEFORE making the request
  inFlightRequests.set(date, {
    promise: fetchPromise,
    timestamp: now
  });
  
  try {
    const data = await fetchPromise;
    return data || [];
  } catch (error) {
    // If this request fails, return empty array
    return [];
  }
};

/**
 * Process news data to get flags and ratings per symbol_id (from API; compound ids use underscore, e.g. AAPL_NYSE).
 * @param {Array} newsData - Array of news items including symbol_id
 * @param {Array<string>} symbolIds - symbol_id values to score
 * @returns {Object} { flags: { [symbol_id]: ... }, ratings: { ... } }
 */
export const processNewsFlags = (newsData, symbolIds) => {
  const flags = {};
  const ratings = {};
  
  if (!symbolIds || !Array.isArray(symbolIds) || symbolIds.length === 0) {
    return { flags, ratings };
  }
  
  if (!newsData || !Array.isArray(newsData) || newsData.length === 0) {
    symbolIds.forEach((id) => {
      if (id) {
        flags[id] = 'grey';
        ratings[id] = null;
      }
    });
    return { flags, ratings };
  }
  
  symbolIds.forEach((symbolId) => {
    if (!symbolId || typeof symbolId !== 'string') {
      return;
    }
    
    const symbolNews = newsData.filter(news =>
      news &&
      news.symbol_id &&
      news.symbol_id === symbolId &&
      news.rating !== null &&
      news.rating !== undefined &&
      typeof news.rating === 'number'
    );
    
    if (symbolNews.length === 0) {
      flags[symbolId] = 'grey';
      ratings[symbolId] = null;
    } else {
      const maxRating = Math.max(...symbolNews.map(n => n.rating));
      ratings[symbolId] = maxRating;
      
      if (maxRating <= 4) {
        flags[symbolId] = 'red';
      } else if (maxRating >= 7) {
        flags[symbolId] = 'green';
      } else if (maxRating === 5 || maxRating === 6) {
        flags[symbolId] = 'amber';
      } else {
        flags[symbolId] = 'grey';
      }
    }
  });
  
  return { flags, ratings };
};
